// languagetools:disable
import type { AppTypeEnum } from '@chaine/types'

import type { TCoordinate } from './address'
import type { TError } from './error'

export type TTrackingDistance = {
  unit?: string | null
  value?: number | null
}

export type TTrackingDateTime = {
  date?: string | null
  time?: string | null
  timezone?: string | null
}

export type TNextStopDetails = {
  distanceToNextStop?: TTrackingDistance | null
  etaToNextStop?: TTrackingDateTime | null
  sequence?: number | null
  stopExternalID?: string | null
  stopID?: string | null
  stopType?: string | null
}

export type TTrackingSourceLastLocationData = {
  city?: string | null
  coordinates?: TCoordinate | null
  countryCode?: string | null
  locationInfo?: Record<string, unknown> | null
  nextStopDetails?: TNextStopDetails | null
  postalCode?: string | null
  stateCode?: string | null
  timestamp?: string | null
}

export type TLocationData<T> = {
  city: string | null

  /**
   * The coordinates of the location.
   */
  coordinates: TCoordinate
  country: string | null
  /**
   * Additional information about the location.
   */
  locationInfo: T
  postalCode: string | null
  stateCode: string | null
  /**
   * The timestamp of the location.
   */
  timestamp: string
  /**
   * Information about the next planned stop relative to this location.
   */
  nextStopDetails?: TNextStopDetails | null
}

/**
 * The source of the tracking data
 * Used for filtering the shipment via tracking status filter
 */
export enum TrackingSourceEnum {
  API = 'API',
  DRIVER_MOBILE_APP = 'DRIVER_MOBILE_APP',
  ELD = 'ELD',
  EXTERNAL_TRACKING_LINK = 'EXTERNAL_TRACKING_LINK',
  SFTP = 'SFTP',
  TIVE_DEVICE = 'TIVE_DEVICE',
  TMS_APP = 'TMS_APP',
  VISIBILITY_PROVIDER = 'VISIBILITY_PROVIDER'
}

/**
 * The source of the tracking data (e.g. 'Mobile App', 'ELD')
 */
export enum ShipmentTrackingSourceEnum {
  API = 'API',
  CSVFile = 'CSV File',
  EDIFile = 'EDI File',
  ELD = 'ELD',
  ExternalTrackingLink = 'External Tracking Link',
  MobileApp = 'Mobile App',
  TMSApp = 'TMS App',
  TiveDevice = 'Tive Device',
  VisibilityProvider = 'Visibility Provider'
}

/**
 * The source of the tracking data (e.g. 'Mobile App', 'ELD' etc.)
 */
export enum ShipmentTrackingSourceForReportsEnum {
  API = 'API',
  CSVFile = 'CSV File',
  Convoy = 'Convoy',
  EDIFile = 'EDI File',
  ELD = 'ELD',
  ExternalTrackingLink = 'External Tracking Link',
  MobileApp = 'Mobile App',
  TMSApp = 'TMS App',
  ThirdPartyCarrierTrackingAPI = 'Third Party Carrier Tracking API',
  TiveDevice = 'Tive Device',
  TrailerNumber = 'Trailer Number',
  TruckNumber = 'Truck Number',
  VisibilityProvider = 'Visibility Provider'
}

export type TShipmentTrackingSource<T, M> = {
  /**
   * The userID of the user who added the tracking source
   */
  addedByUserID?: string
  /**
   * The workspaceID of the workspace who added the tracking source
   */
  addedByWorkspaceID?: string
  /**
   * The reason why the location was filtered (if applicable)
   */
  filterReason?: string | null
  /**
   * Whether the tracking is active or not
   */
  isActive: boolean
  /**
   * The ISO datetime when the location was last fetched from the tracking source
   */
  lastLocationFetchedAt?: string | null
  /**
   * The tracking location data
   */
  locations: TLocationData<T>[] | null
  /**
   * The metadata of the source
   */
  metadata?: M
  /**
   * The userID of the user who removed the external tracking link
   */
  removedByUserID?: string
  /**
   * The userID of the user who removed the external tracking link
   */
  removedByWorkspaceID?: string
  /**
   * The source of the tracking data (e.g. 'Mobile App', 'ELD')
   */
  source: ShipmentTrackingSourceEnum
}

export type TMotionActivityType = 'unknown' | 'still' | 'walking' | 'on_foot' | 'running' | 'on_bicycle' | 'in_vehicle'

export enum AuthorizationStatusEnum {
  AuthorizationStatusAlways = 'Always',
  AuthorizationStatusDenied = 'Denied',
  AuthorizationStatusNotDetermined = 'Not Determined',
  AuthorizationStatusRestricted = 'Restricted',
  AuthorizationStatusUnknown = 'Unknown',
  AuthorizationStatusWhenInUse = 'When In Use'
}

export enum AccuracyAuthorizationEnum {
  AccuracyAuthorizationFull = 'FullAccuracy',
  AccuracyAuthorizationReduced = 'ReducedAccuracy',
  AccuracyAuthorizationUnknown = 'UnknownAccuracy'
}

/**
 * Enum representing the state of a driver-app download SMS action.
 * Keep in sync with the backend enum / GraphQL schema.
 */
export enum DriverAppDownloadMessageStatusEnum {
  Canceled = 'CANCELED',
  Completed = 'COMPLETED',
  Scheduled = 'SCHEDULED'
}

/**
 * Represents a driver-app download SMS action stored in metadata.
 */
export interface IDriverAppDownloadMessageAction {
  actionID: string
  canceledAt?: string | null
  completedAt?: string | null
  messageDueDate: string
  scheduledAt?: string | null
  sequence: number
  status: DriverAppDownloadMessageStatusEnum
  statusDescription?: string | null
}

/**
 * The location settings information if this location was emitted by the `mobileAppLocationSettingsChanged` event.
 */
export type TLocationSettingsType = {
  /**
   * iOS 14+ only
   * The authorization status of location-services. For iOS, this will tell you if the user has enabled "Precise" (Full) or non-precise (Reduced) accuracy.
   * __`[iOS 14+]`__ iOS 14 has introduced a new __`[Precise: On]`__ switch on the location authorization dialog allowing users to disable high-accuracy location.
   *
   * This attribute shows the state of that switch:
   * - Enabled:  [[BackgroundGeolocation.ACCURACY_AUTHORIZATION_FULL]].
   * - Disabled, [[BackgroundGeolocation.ACCURACY_AUTHORIZATION_REDUCED]].
   */
  accuracyAuthorization?: AccuracyAuthorizationEnum

  /**
   * The authorization status of location-services.
   */
  authorizationStatus?: AuthorizationStatusEnum
  /**
   * `true` if GPS geolocation provider is available.
   */
  gpsGeolocationProviderAvailable?: boolean

  /**
   * `true` When device location-services are enabled.
   */
  locationServicesEnabled?: boolean

  /**
   * `true` if network geolocation provider is available.
   */
  networkGeolocationProviderAvailable?: boolean
}

/**
 * The last known motion-activity when this location was recorded.
 */
export interface IMotionActivity {
  /**
   * Confidence of the reported device motion activity in %.
   */
  confidence: number
  /**
   * The reported device motion activity.
   *
   * | Activity Name  |
   * |----------------|
   * | `still`        |
   * | `walking`      |
   * | `on_foot`      |
   * | `running`      |
   * | `on_bicycle`   |
   * | `in_vehicle`   |
   * | `unknown`      |
   */
  type: TMotionActivityType
}

/**
 * The location information for a mobile app location.
 */
export type TMobileAppLocationInfo = {
  /**
   * Accuracy in meters.
   */
  accuracy?: number
  /**
   * Device motion-activity when this location was recorded (eg: `still`, `on_foot`, `in_vehicle`).
   */
  activity?: IMotionActivity
  /**
   * Battery level of the device that emitted the location.
   */
  batteryLevel?: number
  /**
   * Additional information of the device that emitted the location.
   */
  deviceID?: string
  /**
   * Present (and `true`) if the location was generated by a "Fake Location" application or simulator.
   */
  isMockLocation?: boolean
  /**
   * This value will be set to true if the device is moving
   */
  isMoving?: boolean
  /**
   * Universally Unique Identifier. You can use this to match locations recorded at your server with those in the logs.
   * It can also be used to ensure if the plugin has ever posted the same location twice.
   */
  locationID: string
  /**
   * The location settings information if this location was emitted by the `mobileAppLocationSettingsChanged` event.
   */
  locationSettings?: TLocationSettingsType
  /**
   * Speed in meters / second.
   * ⚠️ Note:  Only present when location came from GPS.  `-1` otherwise.
   */
  speed?: number
}

type TConnectionType = 'bluetooth' | 'cellular' | 'ethernet' | 'mixed' | 'none' | 'other' | 'unknown' | 'wifi'

export interface IDeviceInfo {
  /**
   * The Android SDK version number.
   *
   * Only available on Android.
   */
  androidSDKVersion?: number
  /**
   * Driver app version installed on the device.
   */
  appVersion: string
  /**
   * A percentage (0 to 1) indicating how much the battery is charged.
   */
  batteryLevel?: number
  /**
   * The type of network connection the device is using.
   */
  connectionType: TConnectionType
  /**
   * The identifier of the device as available to the app. This identifier may change
   * on modern mobile platforms that only allow per-app install ids.
   *
   * On iOS, the identifier is a UUID that uniquely identifies a device to the app’s vendor ([read more](https://developer.apple.com/documentation/uikit/uidevice/1620059-identifierforvendor)).
   *
   * on Android 8+, __the identifier is a 64-bit number (expressed as a hexadecimal string)__, unique to each combination of app-signing key, user, and device ([read more](https://developer.android.com/reference/android/provider/Settings.Secure#ANDROID_ID)).
   *
   * On web, a random identifier is generated and stored on localStorage for subsequent calls.
   * If localStorage is not available a new random identifier will be generated on every call.
   */
  deviceID: string
  /**
   * The manufacturer of the device.
   */
  deviceManufacturer: string
  /**
   * The device model. For example, "iPhone13,4".
   */
  deviceModel: string
  /**
   * The name of the device. For example, "John's iPhone".
   *
   * This is only supported on iOS and Android 7.1 or above.
   *
   * On iOS 16+ this will return a generic device name without the appropriate [entitlements](https://developer.apple.com/documentation/bundleresources/entitlements/com_apple_developer_device-information_user-assigned-device-name).
   */
  deviceName: string
  /**
   * The operating system of the device.
   * 'ios' | 'android' | 'windows' | 'mac' | 'unknown';
   */
  deviceOS: string
  /**
   * The version of the device OS.
   */
  deviceOSVersion: string
  /**
   * "ios" | "android" | "web"
   */
  devicePlatform: string
  /**
   * The iOS version number.
   * Only available on iOS.
   *
   * Multi-part version numbers are crushed down into an integer padded to two-digits, ex: `"16.3.1"` -> `160301`
   */
  iOSVersion?: number
  /**
   * Whether the device is charging.
   */
  isDeviceCharging: boolean
  /**
   * Whether the app is running in a simulator/emulator.
   */
  isDeviceVirtual: boolean
  /**
   * Whether the device has an active network connection.
   */
  isNetworkConnected: boolean
  /**
   * Two character language code.
   */
  languageCode: string
}

export type TMobileAppTrackingMetadata = {
  /**
   * Ordered list of driver-app download SMS actions (already sorted by the backend).
   */
  appDownloadActions?: IDriverAppDownloadMessageAction[]
  /**
   * The unique ID of the device that emitted the location.
   */
  deviceID: string
  /**
   * Additional information of the device that emitted the location.
   */
  deviceInfo: IDeviceInfo | null
  /**
   * Whether the mobile app has sent mock locations
   */
  hasSentMockLocations: boolean
  /**
   * True if the metadata belongs to a historical device that was used on a past shipment, and not the current one.
   */
  isHistoricalDevice: boolean
  /**
   *  `true` if the user is logged in. Note, if a user logs in and deletes the app, this will remain true as we will have no way of knowing if the user deletes the app
   */
  isLoggedIn?: boolean
  /**
   *  `true` if the user is ON duty within the app. Note that if a user is tracking being ON duty and then subsequently tracking being OFF duty within the app, this status will still remain `true`, as we have no way of knowing if the user has actually gone OFF duty within the app.
   */
  isTrackingOn?: boolean
  /**
   * The last time the user logged in to the app
   */
  lastLogin?: string
  /**
   * The last time the user logged out of the app
   */
  lastLogout?: string
  /**
   * The last time the user was seen on the app (app opened, logged in, logged out)
   */
  lastSeen?: string
  /**
   * The last time the driver stopped tracking (went off duty)
   */
  lastTrackingStoppedAt?: string
  /**
   * The location settings information if this location was emitted by the `mobileAppLocationSettingsChanged` event.
   */
  locationSettings: TLocationSettingsType | null
  notificationAlert: {
    appDownload: {
      numberOfTimesSent: number
    } | null
  }
  /**
   * Last location payload persisted for this source (includes next-stop details).
   */
  lastLocationData?: TTrackingSourceLastLocationData
  /**
   * The user ID of the user who triggered the event
   */
  user: {
    id: string
    name?: string
    phone?: string
    photo?: string
  }
}

export type TExternalTrackingLinkLocationInfo = {
  /**
   * The humidity of the cargo detected by the external tracking device.
   */
  humidity: string | null
  /**
   * The temperature of the cargo detected by the external tracking device.
   */
  temperature: string | number | null
}

export enum ExternalTrackingLinkProviderEnum {
  ATT = 'ATT',
  ATTFleetManagement = 'ATTFleetManagement',
  AnyTrek = 'AnyTrek',
  Emerson = 'Emerson',
  FleetComplete = 'FleetComplete',
  FleetMaster = 'FleetMaster',
  FollowMee = 'FollowMee',
  Geotab = 'Geotab',
  MacroPoint = 'MacroPoint',
  Monarch = 'Monarch',
  Motive = 'Motive',
  OptimaELD = 'Optima ELD',
  Orbcomm = 'Orbcomm',
  Samsara = 'Samsara',
  /* cSpell:disable-next-line */
  TXTELD = 'TXT ELD',
  Tive = 'Tive',
  TrackSmart = 'TrackSmart',
  TruckerTools = 'TruckerTools'
}

/**
 * Used for the return type for the mobile app queries
 */
export type TMobileAppTrackingSource = TShipmentTrackingSource<TMobileAppLocationInfo, TMobileAppTrackingMetadata>

export type TExternalTrackingLinkMetadata = {
  /**
   * Any errors that occurred while fetching the external tracking link
   */
  errors?: TError[]
  /**
   * Whether the tracking URL is supported
   */
  isSupported: boolean
  /**
   * The name of the tracking link provider
   */
  provider: ExternalTrackingLinkProviderEnum | null
  /**
   * Security warning message if the link is considered insecure or hackable
   */
  securityMessage?: string
  /**
   * Last location payload persisted for this source (includes next-stop details).
   */
  lastLocationData?: TTrackingSourceLastLocationData
  /**
   * The URL of the external tracking link
   */
  url: string
}

export type TTiveDeviceLocationInfo = {
  /**
   * The humidity of the cargo detected by the tive device.
   */
  humidity: string | null
  /**
   * The temperature of the cargo detected by the tive device.
   */
  temperature: string | number | null
}

export type TTMSAppLocationInfo = {
  /**
   * The source of the tracking information
   */
  source: ShipmentTrackingSourceEnum
}

export type TVisibilityAppLocationInfo = {
  /**
   * Additional location data from the visibility provider.
   */
  additionalData?: string | null
}

/**
 * Enums for visibility provider tracking
 */
export enum VisibilityTrackingSourceTypeEnum {
  ShipmentNumber = 'ShipmentNumber',
  providerShipmentNumber = 'providerShipmentNumber'
}

export interface IVisibilityTrackingSource {
  type: VisibilityTrackingSourceTypeEnum
  value: string
}

export enum VisibilityProviderEnum {
  FourKites = 'FourKites'
  // Add more visibility providers as needed
}

export type TTiveDeviceMetadata = {
  /**
   * Any errors that occurred while fetching the tive device
   */
  errors?: TError[]
  /**
   * The name of the provider
   */
  provider: string | null
  /**
   * Last location payload persisted for this source (includes next-stop details).
   */
  lastLocationData?: TTrackingSourceLastLocationData
  /**
   * The ID of tive device
   */
  tiveDeviceID: string
}

export type TSFTPLocationInfo = {
  address: {
    city: string | null
    country: string | null
    countryCode: string | null
    serviceProvider: string | null
    state: string | null
    stateCode: string | null
    zip: string | null
  }
  /**
   * This is the same as file type. i.e. 'CSV File' or 'EDI File'
   */
  source: string
}

export type TSFTPSender = {
  dot: string | null
  mcNumber: string | null
  scac: string | null
  sftpUsername: string | null
  trailerNumber: string | null
  truckNumber: string | null
}

export type TSFTPMetadata = {
  /**
   * Specifies the type of file the data was received from
   */
  fileType: 'EDI File' | 'CSV File'
  /**
   * The file sender details
   */
  sender: TSFTPSender
  /**
   * Last location payload persisted for this source (includes next-stop details).
   */
  lastLocationData?: TTrackingSourceLastLocationData
}

export type TAPILocationInfo = {
  /**
   * This is the same as file type. i.e. 'API'
   * The sender will be in sourceID
   */
  source: string
}

export type TAPILocationData = TLocationData<TAPILocationInfo>

export type TAPIMetadata = {
  /**
   * Last location payload persisted for this source (includes next-stop details).
   */
  lastLocationData?: TTrackingSourceLastLocationData
  /**
   * The source ID for the API location source.
   */
  sourceID?: string
}

export type TELDAppLocationInfo = {
  /**
   * The humidity of the cargo detected by the eld app.
   */
  humidity: string | null
  /**
   * The temperature of the cargo detected by the eld app.
   */
  temperature: string | number | null
}

export type TELDAppMetadata = {
  /**
   * The ID of eld app
   */
  deviceID: string
  /**
   * Any errors that occurred while fetching the eld app
   */
  errors?: TError[]
  /**
   * Last location payload persisted for this source (includes next-stop details).
   */
  lastLocationData?: TTrackingSourceLastLocationData
  /**
   * The name of the provider
   */
  provider: string | null
}

export type TTMSAppTrackingMetadata = {
  /**
   * The last location data received
   */
  lastLocationData?: TTrackingSourceLastLocationData
  /**
   * The source ID for the TMS app location
   */
  sourceID: string
}

export type TVisibilityAppLocationData = TLocationData<TVisibilityAppLocationInfo>

export type TVisibilityAppMetadata = {
  /**
   * The Device ID of the visibility app device
   */
  deviceID: string
  /**
   * The last location data of the visibility app
   */
  lastLocationData?: TTrackingSourceLastLocationData

  /**
   * The name of the tracking link provider
   */
  provider?: AppTypeEnum

  /**
   * The source ID for the visibility location source.
   */
  sourceID?: string

  /**
   * The tracking source of the visibility app
   */
  trackingSource?: IVisibilityTrackingSource
}

export type TMobileAppLocationData = TLocationData<TMobileAppLocationInfo>

export type TExternalTrackingLinkLocationData = TLocationData<TExternalTrackingLinkLocationInfo>

export type TSFTPLocationData = TLocationData<TSFTPLocationInfo>

export type TELDAppLocationData = TLocationData<TELDAppLocationInfo>

export type TTiveDeviceLocationData = TLocationData<TTiveDeviceLocationInfo>

export type TTMSAppLocationData = TLocationData<TTMSAppLocationInfo>

/**
 * Used for the return type for the external tracking link queries
 */
export type TExternalTrackingLinkSource = TShipmentTrackingSource<
  TExternalTrackingLinkLocationInfo,
  TExternalTrackingLinkMetadata
>

/**
 * Used for the return type for the tive device link queries
 */
export type TSFTPSource = TShipmentTrackingSource<TSFTPLocationInfo, TSFTPMetadata>

/**
 * Used for the return type for the tive device link queries
 */
export type TTiveDeviceSource = TShipmentTrackingSource<TTiveDeviceLocationInfo, TTiveDeviceMetadata>

/**
 * Used for the return type for the tive device link queries
 */
export type TAPISource = TShipmentTrackingSource<TAPILocationInfo, TAPIMetadata>

/**
 * Represents a TMS App tracking source
 */
export type TTMSAppSource = TShipmentTrackingSource<TTMSAppLocationInfo, TTMSAppTrackingMetadata> & {
  /**
   * The timestamp when the tracking source was added
   */
  addedAt: string
  /**
   * The tracking source display name
   */
  sourceDisplayName: string
  /**
   * The tracking source ID
   */
  sourceID: string
  /**
   * The tracking source type
   */
  sourceType: string
}

/**
 * Used for the return type for the shipment visibility app queries
 */
export type TVisibilityAppSource = TShipmentTrackingSource<TVisibilityAppLocationInfo, TVisibilityAppMetadata>

/**
 * Used for the return type for the eld app link queries
 */
export type TELDAppSource = TShipmentTrackingSource<TELDAppLocationInfo, TELDAppMetadata>

export type TShipmentTrackingSourceTypes =
  | TMobileAppTrackingSource
  | TExternalTrackingLinkSource
  | TTiveDeviceSource
  | TSFTPSource
  | TAPISource
  | TELDAppSource
  | TTMSAppSource
  | TVisibilityAppSource

/**
 * A union representing types for a tracking source's location data (e.g. MobileAppLocationData, ExternalTrackingLinkLocationData)
 */
export type TLocationDataTypes =
  | TAPILocationData
  | TMobileAppLocationData
  | TExternalTrackingLinkLocationData
  | TSFTPLocationData
  | TELDAppLocationData
  | TTMSAppLocationData
  | TVisibilityAppLocationData

/**
 * A union representing types for a tracking source's metadata (e.g. MobileAppTrackingMetadata, ExternalTrackingLinkMetadata)
 */
export type TTrackingSourceMetadataTypes =
  | TMobileAppTrackingMetadata
  | TExternalTrackingLinkMetadata
  | TTiveDeviceMetadata
  | TSFTPMetadata
  | TAPIMetadata
  | TELDAppMetadata
  | TTMSAppTrackingMetadata
  | TVisibilityAppMetadata
  | undefined
